'use client'

import { useEffect, useRef } from 'react'

import { getMediaUrl } from '@/utilities/getMediaUrl'
import { cn } from '@/utilities/ui'

import type { Media } from '@/payload-types'

function resolveVideoSrc(video: Media): string {
  if (video.url) return getMediaUrl(video.url, video.updatedAt)
  if (video.filename) return getMediaUrl(`/media/${video.filename}`)
  return ''
}

type CollectionVideoPlayerProps = {
  video: Media
  className?: string
  onEnded?: () => void
  onNearComplete?: () => void
}

export const CollectionVideoPlayer: React.FC<CollectionVideoPlayerProps> = ({
  video,
  className,
  onEnded,
  onNearComplete,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const nearCompleteFiredRef = useRef(false)
  const onEndedRef = useRef(onEnded)
  const onNearCompleteRef = useRef(onNearComplete)
  const src = resolveVideoSrc(video)

  useEffect(() => {
    onEndedRef.current = onEnded
    onNearCompleteRef.current = onNearComplete
  }, [onEnded, onNearComplete])

  useEffect(() => {
    nearCompleteFiredRef.current = false
  }, [src])

  if (!src) return null

  return (
    <video
      ref={videoRef}
      key={src}
      src={src}
      controls
      playsInline
      preload="metadata"
      className={cn('size-full object-cover', className)}
      onEnded={() => onEndedRef.current?.()}
      onTimeUpdate={() => {
        const el = videoRef.current
        if (!el || nearCompleteFiredRef.current) return
        const duration = el.duration
        if (duration && el.currentTime / duration > 0.9) {
          nearCompleteFiredRef.current = true
          onNearCompleteRef.current?.()
        }
      }}
    />
  )
}
